import ky from 'ky';

import { USER_AGENT } from '@/constants/env';

import type { KyInstance } from 'ky';

import { reqCatalogs } from './catalogs/reqCatalogs';
import { reqCreateOrder } from './createOrder/reqCreateOrder';
import { reqDashboard } from './dashboard/reqDashboard';
import { reqInventory } from './Fulfill/Inventory/reqInventory';
import { reqHistoryOrders } from './Fulfill/inventoryHistory/reqHistory';
import { reqLotTracking } from './Fulfill/LotTracking/reqLotTracking';
import { reqStatusOrders } from './Fulfill/StatusOrders/reqStatusOrders';
import { reqFulfillUsers } from './Fulfill/Users/reqUsers';
import { reqWarehouses } from './Fulfill/Warehouses/reqWarehouses';
import { reqNotifications } from './notifications/reqNotifications';
import { reqOrders } from './orders/reqOrders';
import { reqEnvioFlex } from './plugins/EnvioFlex/reqEnvioFlex';
import { reqFalabella } from './plugins/Falabella/reqFalabella';
import { reqMLFulfill } from './plugins/MLFulfill/reqMLFulfill';
import { reqMLParcel } from './plugins/MLParcel/reqMLParcel';
import { reqUPS } from './ups/reqUPS';
import { reqUserData } from './userData/reqUserData';
import { createReqUserSession } from './userSession/reqUserSession';
import { reqWebhooks } from './webhooks/reqWebhooks';

// Dynamic token resolver for Auth0
let tokenResolver: (() => Promise<string | null>) | null = null;

export function setTokenResolver(resolver: (() => Promise<string | null>) | null) {
  tokenResolver = resolver;
}

export function getTokenResolver() {
  return tokenResolver;
}

/**
 * Helper function to create KyInstance with configurable timeout
 *
 * @param prefixUrl - Base URL for all requests
 * @param token - Authorization token (optional)
 * @param timeout - Request timeout in milliseconds (opcional, si no se provee usa el default de ky: 10000ms)
 * @returns Configured KyInstance
 */
export function createHttpInstance(
  prefixUrl: string,
  token: string | null,
  timeout?: number,
  isAuth0?: boolean,
  extraHeaders?: Record<string, string>,
): KyInstance {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'User-Agent': USER_AGENT,
    ...extraHeaders,
  };

  if (token && !isAuth0) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const config: {
    prefix: string;
    headers: Record<string, string>;
    timeout?: number;
    hooks?: Record<
      string,
      ((state: {
        request: Request;
      }) => void | Promise<void> | Request | Response | Promise<Request | Response>)[]
    >;
  } = {
    prefix: prefixUrl,
    headers,
  };

  if (timeout !== undefined) {
    config.timeout = timeout;
  }

  if (isAuth0) {
    config.hooks = {
      beforeRequest: [
        async ({ request }: { request: Request }) => {
          if (tokenResolver) {
            try {
              const freshToken = await tokenResolver();
              if (freshToken) {
                request.headers.set('Authorization', `Bearer ${freshToken}`);
              }
            } catch (error) {
              console.error('Error resolving Auth0 token inside Ky hook:', error);
            }
          }
        },
      ],
    };
  }

  return ky.create(config);
}

// Helper function to initialize HUB services
export function initializeHubServices(httpInstance: KyInstance) {
  return {
    userSession: createReqUserSession(httpInstance),
    userData: reqUserData(httpInstance),
    catalogs: reqCatalogs(httpInstance),
    createOrder: reqCreateOrder(httpInstance),
    notifications: reqNotifications(httpInstance),
    orders: reqOrders(httpInstance),
    webhooks: reqWebhooks(httpInstance),
    dashboard: reqDashboard(httpInstance),
  };
}

// Helper function to initialize FF services
export function initializeFFServices(httpInstance: KyInstance) {
  return {
    inventory: reqInventory(httpInstance),
    statusOrder: reqStatusOrders(httpInstance),
    historyOrder: reqHistoryOrders(httpInstance),
    warehouses: reqWarehouses(httpInstance),
    fulfillUsers: reqFulfillUsers(httpInstance),
    lotTracking: reqLotTracking(httpInstance),
  };
}

// Helper function to initialize UPS services
export function initializeUpsServices(httpInstance: KyInstance) {
  return {
    ups: reqUPS(httpInstance),
  };
}

// Helper function to initialize Plugin services
export function initializePluginServices(httpInstance: KyInstance) {
  return {
    envioFlex: reqEnvioFlex(httpInstance),
    falabella: reqFalabella(httpInstance),
    mlFulfill: reqMLFulfill(httpInstance),
    mlParcel: reqMLParcel(httpInstance),
  };
}
